import React, { useCallback, useEffect, useContext } from 'react';
import {
  useLabelsHeaderCellQuery,
  LabelsHeaderCellQuery,
} from './LabelsHeaderCellQuery.generated';
import { HeaderCellButton } from './HeaderCellButton';
import { Popover, usePopover, PopoverScreen } from '@trello/nachos/popover';
import { forTemplate } from '@trello/i18n';

import { SavedViewContext, SavedViewEditable } from './SavedViewContext';
import { Analytics } from '@trello/atlassian-analytics';
import { LabelsFilterPopoverContent } from './filters/LabelsFilterPopoverContent';

const format = forTemplate('multi_board_table_view');

enum Screen {
  LabelsFilterScreen,
}

interface LabelsHeaderCellProps {
  idBoards: string[];
  idOrg: string;
}

export const getUniqueLabels = (
  boards: NonNullable<LabelsHeaderCellQuery['organization']>['boards'],
) => {
  const labelsMap = new Map<string | null, Set<string>>();
  return boards.reduce((acc, curr) => {
    const uniqueLabelsFromBoard = curr.labels.filter((label) => {
      const color = label.color || null;
      const colorSet = labelsMap.get(color);
      if (!colorSet) {
        labelsMap.set(
          color,
          new Set<string>([label.name]),
        );

        return true;
      }

      if (!colorSet.has(label.name)) {
        colorSet.add(label.name);
        return true;
      }

      return false;
    });

    return [...acc, ...uniqueLabelsFromBoard];
  }, []);
};

const LabelseaderCellEditable: React.FC<
  LabelsHeaderCellProps & { savedView: SavedViewEditable }
> = ({ idBoards, idOrg, savedView }) => {
  const { toggle, triggerRef, popoverProps } = usePopover<HTMLDivElement>({
    initialScreen: Screen.LabelsFilterScreen,
  });

  useEffect(() => {
    if (popoverProps.isVisible) {
      Analytics.sendScreenEvent({
        name: 'labelFilterInlineDialog',
        containers: {
          organization: {
            id: idOrg,
          },
        },
      });
    }
  }, [popoverProps.isVisible, idOrg]);

  const { data } = useLabelsHeaderCellQuery({
    fetchPolicy: 'cache-only',
    variables: {
      idOrg,
      idBoards,
    },
  });

  const toggleLabelPopover = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      source: 'multiBoardTableViewScreen',
      buttonName: 'filterTableByLabelButton',
      containers: {
        organization: {
          id: idOrg,
        },
      },
    });

    toggle();
  }, [toggle, idOrg]);

  if (!data?.organization?.boards || !data?.member) {
    throw new Error('Failed to get label header cell details from cache.');
  }

  return (
    <>
      <HeaderCellButton
        heading={format('labels')}
        onClick={toggleLabelPopover}
        isClickable
        isFilterActive={savedView.filters.labels.filterLength() !== 0}
        isSortingActive={false}
        forwardedRef={triggerRef}
        isActive={popoverProps.isVisible}
      />
      <Popover
        title={format('labels')}
        dangerous_disableHideOnUrlSearchParamsChange
        dontOverlapAnchorElement
        {...popoverProps}
      >
        <PopoverScreen id={Screen.LabelsFilterScreen}>
          <LabelsFilterPopoverContent
            idOrg={idOrg}
            showResetButton={true}
            popoverTarget="headerCell"
            labelsFilter={savedView.filters.labels}
            setLabelsFilter={savedView.setFilter}
            boards={data?.organization?.boards}
            member={data?.member}
          ></LabelsFilterPopoverContent>
        </PopoverScreen>
      </Popover>
    </>
  );
};

export const LabelsHeaderCell: React.FunctionComponent<LabelsHeaderCellProps> = ({
  idBoards,
  idOrg,
}) => {
  const { savedView } = useContext(SavedViewContext);

  return savedView.editable ? (
    <LabelseaderCellEditable
      idBoards={idBoards}
      idOrg={idOrg}
      savedView={savedView}
    />
  ) : (
    <HeaderCellButton
      heading={format('labels')}
      isFilterActive={savedView.filters.labels.filterLength() !== 0}
      isSortingActive={false}
    />
  );
};
